import type { CrawlJobStatusDTO, CrawlStatus } from '../../types/knowledge';

type StatusCount = { count?: number | null; status?: string | null };

export const CRAWL_PAGE_STATUSES = [
  'PENDING',
  'PROCESSING',
  'COMPLETED',
  'FAILED',
] as const;

export const CRAWL_PAGE_DONE_STATUSES = ['COMPLETED', 'FAILED'] as const;

export const CRAWL_STAGING_DOCUMENT_STATUSES = [
  'PENDING',
  'SCANNING',
  'UPLOADING',
  'EXTRACTING',
  'EMBEDDING',
  'READY',
  'FAILED',
] as const;

export const CRAWL_STAGING_DOCUMENT_DONE_STATUSES = [
  'READY',
  'FAILED',
] as const;

function countByStatus(
  counts: ReadonlyArray<StatusCount> | null | undefined,
): Map<string, number> {
  const map = new Map<string, number>();

  for (const entry of counts ?? []) {
    if (entry.status != null) {
      map.set(entry.status, entry.count ?? 0);
    }
  }

  return map;
}

function sumStatusCounts(
  counts: ReadonlyArray<StatusCount> | null | undefined,
): {
  total: number;
  completed: number;
} {
  if (!counts?.length) {
    return { total: 0, completed: 0 };
  }

  let total = 0;
  let completed = 0;

  for (const entry of counts) {
    const count = entry.count ?? 0;
    total += count;
    if (entry.status === 'COMPLETED' || entry.status === 'READY') {
      completed += count;
    }
  }

  return { total, completed };
}

export type CrawlSectionProgress = {
  done: number;
  total: number;
  percent: number;
};

export function getCrawlSectionProgress(
  counts: ReadonlyArray<StatusCount> | null | undefined,
  options: {
    statuses: readonly string[];
    doneStatuses: readonly string[];
  },
): CrawlSectionProgress | null {
  if (counts == null) {
    return null;
  }

  const byStatus = countByStatus(counts);
  let total = 0;
  let done = 0;

  for (const status of options.statuses) {
    const count = byStatus.get(status) ?? 0;
    total += count;
    if (options.doneStatuses.includes(status)) {
      done += count;
    }
  }

  const percent = total > 0 ? Math.round((done / total) * 100) : 0;

  return { done, total, percent };
}

export function getCrawlPageSectionProgress(
  liveStatus: CrawlJobStatusDTO | null | undefined,
): CrawlSectionProgress | null {
  return getCrawlSectionProgress(liveStatus?.pageStatusCounts, {
    statuses: CRAWL_PAGE_STATUSES,
    doneStatuses: CRAWL_PAGE_DONE_STATUSES,
  });
}

export function getCrawlStagingDocumentSectionProgress(
  liveStatus: CrawlJobStatusDTO | null | undefined,
): CrawlSectionProgress | null {
  return getCrawlSectionProgress(liveStatus?.stagingDocumentStatusCounts, {
    statuses: CRAWL_STAGING_DOCUMENT_STATUSES,
    doneStatuses: CRAWL_STAGING_DOCUMENT_DONE_STATUSES,
  });
}

/** Average of page and staging-document percentages shown in crawl detail lines. */
export function getCrawlOverallProgressPercent(
  liveStatus: CrawlJobStatusDTO | null | undefined,
): number | null {
  const pageProgress = getCrawlPageSectionProgress(liveStatus);
  const documentProgress = getCrawlStagingDocumentSectionProgress(liveStatus);

  if (pageProgress == null && documentProgress == null) {
    return null;
  }

  const pagePercent = pageProgress?.percent ?? 0;
  const documentPercent = documentProgress?.percent ?? 0;

  return Math.round((pagePercent + documentPercent) / 2);
}

export type CrawlProgressSnapshot = {
  labelKey: string;
  labelParams?: Record<string, string>;
  percentage: number | null;
  completed: number;
  total: number;
};

export function getCrawlProgressSnapshot(
  status: CrawlStatus,
  liveStatus: CrawlJobStatusDTO | null | undefined,
): CrawlProgressSnapshot | null {
  if (status === 'MAPPING') {
    return {
      labelKey: 'knowledge.crawlProgressMapping',
      percentage: null,
      completed: 0,
      total: 0,
    };
  }

  if (status === 'AWAITING_SELECTION') {
    return {
      labelKey: 'knowledge.crawlProgressAwaitingSelection',
      percentage: null,
      completed: 0,
      total: 0,
    };
  }

  if (status === 'CRAWLING') {
    const { total, completed } = sumStatusCounts(liveStatus?.pageStatusCounts);
    return {
      labelKey: 'knowledge.crawlProgressPages',
      labelParams: {
        completed: String(completed),
        total: String(total),
      },
      percentage: total > 0 ? Math.round((completed / total) * 100) : null,
      completed,
      total,
    };
  }

  if (status === 'PROCESSING') {
    const staging = sumStatusCounts(liveStatus?.stagingDocumentStatusCounts);
    const published = sumStatusCounts(
      liveStatus?.publishedDocumentStatusCounts,
    );
    const completed = staging.completed + published.completed;
    const total = staging.total + published.total;

    return {
      labelKey: 'knowledge.crawlProgressDocuments',
      labelParams: {
        completed: String(completed),
        total: String(total),
      },
      percentage: total > 0 ? Math.round((completed / total) * 100) : null,
      completed,
      total,
    };
  }

  return null;
}
